import { Fragment, useEffect, useMemo, type FC } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useFormik } from 'formik';
import { boolean, object, Schema, string } from 'yup';
import { useShallow } from 'zustand/react/shallow';
import {
  ButtonField,
  CheckboxField,
  InputField,
  SelectField,
  Smokebreak,
} from '@wk/components-react16';

import { SimpleEditor } from '@/editor/tiptap/components/tiptap-templates/simple/simple-editor';

import type {
  Evaluation,
  EvaluationCheckpoint,
  JiraAllowedValue,
  JiraCreateMetaField,
  JiraProject,
} from '@/types';

import { createJiraIssue, getJiraProjects } from '@/domains/jira/jiraApi';
import { clean, getADF, getDomainFromUrl } from '@/lib/utils';

import './ReportBugDrawer.css';
import useToast from '@/shared/stores/toastStore';
import { IconLoader2 } from '@tabler/icons-react';

interface ReportBugProps {
  isOpen: boolean;
  onClose: () => void;
  evaluation: Evaluation | null;
  checkpoint: EvaluationCheckpoint | null;
}

const accountableTeamsJIRAKey: Record<string, string> = {
  'Infrastructure Portfolio Management': 'PCOM',
  'Architecture & Engineering Services': 'PCOM',
  'Global Information Security': 'CTC',
  'Cybersecurity & Technology Controls': 'CTC',
  'Global Hosting': 'GHCOM',
  'Disaster Recovery': 'DRCOM',
  'Infrastructure Operations': 'PCOM',
  'Application Operations': 'PCOM',
};

const excludedFields = [
  'project',
  'summary',
  'description',
  'reporter',
  'issuetype',
];

const ReportBug: FC<ReportBugProps> = ({
  isOpen,
  onClose,
  evaluation,
  checkpoint,
}) => {
  const { accounts } = useMsal();
  const { showToast } = useToast(
    useShallow((state) => ({
      showToast: state.showToast,
    })),
  );

  const email = useMemo(() => {
    const account = accounts[0];
    const claimEmail =
      (account?.idTokenClaims?.email as string | undefined) ||
      (account?.idTokenClaims?.preferred_username as string | undefined);
    return (claimEmail || account?.username || '').toLowerCase();
  }, [accounts]);

  const jiraKey = useMemo(() => {
    if (!checkpoint) return null;
    return accountableTeamsJIRAKey[checkpoint.metadata.accountable];
  }, [checkpoint]);

  const { data: jiraProjects } = useQuery({
    enabled: isOpen,
    queryKey: ['jiraProjects'],
    queryFn: getJiraProjects,
  });

  const { mutate: createIssueMutation, isPending: isCreatingIssue } =
    useMutation({
      mutationFn: createJiraIssue,
    });

  const activeProject = useMemo<JiraProject | undefined>(() => {
    if (!jiraProjects || !Array.isArray(jiraProjects)) return undefined;
    return jiraProjects.find((project) => project.key === jiraKey);
  }, [jiraProjects, jiraKey]);

  const activeProjectRequiredFields = useMemo(() => {
    return activeProject?.createmeta.fields.filter(
      (field: JiraCreateMetaField) =>
        field.required && !excludedFields.includes(field.name.toLowerCase()),
    );
  }, [activeProject]);

  const activeProjectPriorityField = useMemo(() => {
    const field = activeProject?.createmeta.fields.find(
      (field: JiraCreateMetaField) =>
        field.name.toLowerCase() === 'priority' ||
        field.name.toLowerCase() === 'severity',
    );
    return field && !field.required ? field : undefined;
  }, [activeProject]);

  const getFieldValue = (field: JiraCreateMetaField) => {
    switch (field.schema.type) {
      case 'string':
      case 'number':
      case 'date':
      case 'datetime':
        return values[field.fieldId] as string;
      case 'array':
        return (values[field.fieldId] as JiraAllowedValue[]).map(
          (option: JiraAllowedValue) => option.id,
        );
      case 'user':
      case 'project':
      case 'issuetype':
      case 'priority':
      case 'option':
      case 'resolution':
      case 'status':
      case 'version':
      case 'component':
        return { id: values[field.fieldId] as string };
      // case 'timetracking':
      // Handle time tracking fields (this might need custom logic)
      // return bugForm[field.fieldId];
      default:
        console.warn(`Unhandled field type: ${field.schema.type}`);
        return null;
    }
  };

  const {
    values,
    setFieldValue,
    handleSubmit,
    handleBlur,
    handleChange,
    isValid,
    resetForm,
  } = useFormik<{
    title: string;
    description: string;
    expectedBehavior: string;
    [key: string]: string | boolean | JiraAllowedValue | JiraAllowedValue[];
  }>({
    initialValues: {
      title: `Bug - ${evaluation?.metadata.bitId} - ${checkpoint?.metadata.displayName}`,
      description: '',
      expectedBehavior: '',
      [activeProjectPriorityField?.fieldId || '']: '',
      ...activeProjectRequiredFields?.reduce(
        (acc, field) => {
          acc[field.fieldId] = field.schema.type === 'boolean' ? false : '';
          return acc;
        },
        {} as Record<string, string | boolean>,
      ),
    },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: object({
      title: string().required('Title is required'),
      description: string().required('Description is required'),
      expectedBehavior: string().required('Expected Behavior is required'),
      [activeProjectPriorityField?.fieldId || '']: string().required(
        `${activeProjectPriorityField?.name} is required`,
      ),
      ...activeProjectRequiredFields?.reduce(
        (acc, field) => {
          acc[field.fieldId] =
            field.schema.type === 'boolean'
              ? boolean()
              : string().required(`${field.name} is required`);
          return acc;
        },
        {} as Record<string, Schema>,
      ),
    }),
    onSubmit: (values) => {
      const description = getADF(`
          <h3>Description</h3>
          ${values.description}
          ---
          <h3>Expected Behavior</h3>
          ${values.expectedBehavior}
          ---
          <p><b>Reporter:</b> ${email}</p>
          <p><b>Evaluation Url:</b> <a href="${window.location.href}">${evaluation?.metadata.bitId} - ${checkpoint?.metadata.displayName}</a></p>
          ---
        `);
      const data = {
        summary: values.title,
        description,
        project: activeProject?.id,
        [activeProjectPriorityField?.fieldId || '']: {
          id: values[activeProjectPriorityField?.fieldId || ''],
        },
        ...activeProjectRequiredFields?.reduce(
          (acc, field) => {
            acc[field.fieldId] = getFieldValue(field);
            return acc;
          },
          {} as Record<string, unknown>,
        ),
        reporter: email || undefined,
      };
      createIssueMutation(clean(data), {
        onSuccess: (data) => {
          const url = `https://${getDomainFromUrl(data.self)}/browse/${data.key}`;
          onClose();
          showToast('success', {
            title: 'Issue created',
            message: 'Issue created successfully',
            actions: [
              {
                label: 'View Issue',
                onClick: () => {
                  window.open(url, '_blank');
                },
              },
            ],
          });
        },
        onError: (e) => {
          console.log(e);

          showToast('error', {
            title: 'Error',
            message: 'Failed to create issue contact Pegasus team for support',
          });
        },
      });
    },
  });

  const renderField = (field: JiraCreateMetaField) => {
    if (['string', 'number', 'date'].includes(field.schema.type)) {
      return (
        <InputField label={field.name}>
          <input
            name={field.fieldId}
            type={field.schema.type}
            value={values[field.fieldId] as string}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </InputField>
      );
    }
    if (field.schema.type === 'boolean') {
      return (
        <CheckboxField label={field.name}>
          <input
            name={field.fieldId}
            type="checkbox"
            checked={values[field.fieldId] as boolean}
            onChange={(event) =>
              setFieldValue(field.fieldId, event.target.checked)
            }
          />
        </CheckboxField>
      );
    }
    if (
      [
        'array',
        'user',
        'project',
        'issuetype',
        'priority',
        'option',
        'resolution',
        'status',
        'version',
        'component',
      ].includes(field.schema.type) &&
      field.allowedValues &&
      field.allowedValues.length > 0
    ) {
      return (
        <SelectField label={field.name}>
          <select
            value={values[field.fieldId] as string}
            name={field.fieldId}
            id={field.fieldId}
            onBlur={handleBlur}
            onChange={handleChange}
          >
            <option value="">Select {field.name}</option>
            {field.allowedValues.map((value) => (
              <option key={value.id} value={value.id}>
                {value.name || value.value}
              </option>
            ))}
          </select>
        </SelectField>
      );
    }
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  return (
    <div className="reportBug">
      <Smokebreak isOpen={isOpen} onUserRequest={onClose} position="right">
        <h2 slot="smokebreakHeader" className="p-3 font-semibold text-lg">
          Report Bug - {evaluation?.metadata.bitId} -{' '}
          {checkpoint?.metadata.displayName}
        </h2>
        <div slot="smokebreakBody" className="w-full h-full px-4">
          <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit}>
            <InputField label="Title">
              <input
                type="text"
                name="title"
                value={values.title}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </InputField>
            <div className="w-full cg-field">
              <label htmlFor="description" className="cg-field-label">
                Description
              </label>
              <div className="w-full group border border-input hover:border-[#005b92] hover:shadow-[0_0_0_.0625rem_#005b92] focus-within:border-[#000] focus-within:shadow-[0_0_0_.0625rem_#000] focus-within:hover:border-[#000] focus-within:hover:shadow-[0_0_0_.0625rem_#000] text-sm reportBug__editor">
                <SimpleEditor
                  value={values.description}
                  onChange={(value) => setFieldValue('description', value)}
                  placeholder="Describe what's happening..."
                />
              </div>
            </div>
            <div className="w-full cg-field">
              <label htmlFor="description" className="cg-field-label">
                Expected Behavior / Outcome
              </label>
              <div className="w-full group border border-input hover:border-[#005b92] hover:shadow-[0_0_0_.0625rem_#005b92] focus-within:border-[#000] focus-within:shadow-[0_0_0_.0625rem_#000] focus-within:hover:border-[#000] focus-within:hover:shadow-[0_0_0_.0625rem_#000] text-sm reportBug__editor">
                <SimpleEditor
                  value={values.expectedBehavior}
                  onChange={(value) => setFieldValue('expectedBehavior', value)}
                  placeholder="Tell us what should happen..."
                />
              </div>
            </div>
            {activeProjectPriorityField && (
              <SelectField label={activeProjectPriorityField.name}>
                <select
                  name={activeProjectPriorityField.name}
                  id={activeProjectPriorityField.name}
                  value={values[activeProjectPriorityField.fieldId] as string}
                  onChange={(e) =>
                    setFieldValue(
                      activeProjectPriorityField.fieldId,
                      e.target.value,
                    )
                  }
                >
                  <option value="">
                    Select {activeProjectPriorityField.name}
                  </option>
                  {activeProjectPriorityField.allowedValues.map((value) => (
                    <option key={value.id} value={value.id}>
                      {value.name || value.value}
                    </option>
                  ))}
                </select>
              </SelectField>
            )}
            {activeProjectRequiredFields &&
              activeProjectRequiredFields.length > 0 &&
              activeProjectRequiredFields.map((field) => (
                <Fragment key={field.fieldId}>{renderField(field)}</Fragment>
              ))}
            <div className="w-full [&>div]:w-full">
              <ButtonField>
                <button
                  className="w-full flex justify-center"
                  type="submit"
                  disabled={!isValid || isCreatingIssue}
                >
                  {isCreatingIssue ? (
                    <IconLoader2 className="animate-spin" />
                  ) : (
                    'Submit'
                  )}
                </button>
              </ButtonField>
            </div>
          </form>
        </div>
      </Smokebreak>
    </div>
  );
};

export default ReportBug;
